/**
 * Street View for camera locations.
 *
 * - Settings come from `/admin/system-settings/camera-street-view` (readable by
 *   any signed-in user, writable by admins) and are cached module-wide so every
 *   popup/filmstrip shares one request.
 * - Deep links are built client-side and never need a key.
 * - Thumbnails go through the backend proxy (`/camera-overlays/street-view/image`)
 *   so the Google key stays server-side. `<img src>` can't carry our Bearer
 *   token, so images are fetched as blobs and handed out as object URLs.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiService } from './api';
import type { CameraLocationProperties } from './cameraOverlayService';

const http = apiService.client;

export type CameraStreetViewSettings = {
  enabled: boolean;
  embed: boolean;
  /** Server has a Google key, so inline thumbnails can actually render. */
  embed_available: boolean;
  in_view_limit: number;
  provider: string;
  updated_at?: string | null;
  updated_by?: string | null;
  /** Persistent thumbnail cache stats (admins only). */
  cache?: StreetViewCacheStats | null;
};

export type StreetViewCacheStats = {
  available: boolean;
  /** Rows holding an image. */
  images: number;
  /** Rows remembering "no imagery here". */
  negatives: number;
  bytes: number;
  hits: number;
  entries?: number;
  oldest?: string | null;
  newest?: string | null;
};

export const CAMERA_STREET_VIEW_DEFAULTS: CameraStreetViewSettings = {
  enabled: false,
  embed: true,
  embed_available: false,
  in_view_limit: 12,
  provider: 'google',
};

export type CameraStreetViewSettingsPatch = Partial<Pick<CameraStreetViewSettings, 'enabled' | 'embed' | 'in_view_limit'>>;

/** True when inline thumbnails should be attempted (all three gates open). */
export function streetViewPreviewsOn(s: CameraStreetViewSettings | null | undefined): boolean {
  return Boolean(s && s.enabled && s.embed && s.embed_available);
}

// ── Settings cache ───────────────────────────────────────────────────────────

const SETTINGS_TTL_MS = 60_000;
const SETTINGS_PATH = '/admin/system-settings/camera-street-view';

let cached: CameraStreetViewSettings | null = null;
let cachedAt = 0;
let inFlight: Promise<CameraStreetViewSettings> | null = null;
const listeners = new Set<(s: CameraStreetViewSettings) => void>();

function publish(next: CameraStreetViewSettings) {
  cached = next;
  cachedAt = Date.now();
  listeners.forEach((fn) => fn(next));
}

export async function fetchCameraStreetViewSettings(force = false): Promise<CameraStreetViewSettings> {
  if (!force && cached && Date.now() - cachedAt < SETTINGS_TTL_MS) return cached;
  if (inFlight) return inFlight;
  inFlight = http
    .get<CameraStreetViewSettings>(SETTINGS_PATH, { timeout: 20_000 })
    .then((r) => {
      const next = { ...CAMERA_STREET_VIEW_DEFAULTS, ...r.data };
      publish(next);
      return next;
    })
    .catch((err) => {
      // 401/403/5xx: fall back to "off" so the UI simply hides the feature.
      const next = cached ?? CAMERA_STREET_VIEW_DEFAULTS;
      if (!cached) publish(next);
      throw err;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export async function updateCameraStreetViewSettings(
  patch: CameraStreetViewSettingsPatch,
): Promise<CameraStreetViewSettings> {
  const r = await http.put<CameraStreetViewSettings>(SETTINGS_PATH, patch, { timeout: 20_000 });
  const next = { ...CAMERA_STREET_VIEW_DEFAULTS, ...r.data };
  publish(next);
  return next;
}

/** Admin: drop cached thumbnails (all, or only the "no imagery" negatives). */
export async function clearCameraStreetViewCache(negativesOnly = false): Promise<CameraStreetViewSettings> {
  const r = await http.delete<CameraStreetViewSettings>(`${SETTINGS_PATH}/cache`, {
    params: negativesOnly ? { negatives_only: true } : undefined,
    timeout: 60_000,
  });
  const next = { ...CAMERA_STREET_VIEW_DEFAULTS, ...r.data };
  publish(next);
  return next;
}

/** Shared, cached Street View settings. `settings` is null until first load. */
export function useCameraStreetViewSettings(): {
  settings: CameraStreetViewSettings | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [settings, setSettings] = useState<CameraStreetViewSettings | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listeners.add(setSettings);
    return () => {
      listeners.delete(setSettings);
    };
  }, []);

  const refresh = useCallback(async (force = true) => {
    setLoading(true);
    setError(null);
    try {
      await fetchCameraStreetViewSettings(force);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } }; message?: string })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e as Error)?.message || 'Failed to load Street View settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(false);
  }, [refresh]);

  return { settings, loading, error, refresh: () => refresh(true) };
}

// ── Heading helpers (mirror backend camera_street_view.parse_heading) ────────

const CARDINALS: Record<string, number> = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5, E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5, W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
  NORTH: 0, EAST: 90, SOUTH: 180, WEST: 270,
};

const norm = (d: number) => ((d % 360) + 360) % 360;

/** OSM `direction` / `camera:direction` → degrees 0–360, or null. */
export function parseCameraHeading(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? norm(value) : null;
  const raw = String(value).trim();
  if (!raw) return null;
  // "45;90" / "45,90" → first entry
  const first = raw.split(/[;,]/)[0].trim();
  const upper = first.toUpperCase();
  if (upper in CARDINALS) return CARDINALS[upper];
  const range = first.match(/^(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)$/);
  if (range) {
    const a = Number(range[1]);
    const b = Number(range[2]);
    const span = norm(b - a);
    return norm(a + span / 2);
  }
  const num = first.match(/-?\d+(?:\.\d+)?/);
  if (num) return norm(Number(num[0]));
  return null;
}

/** Heading for a camera from its mapped attributes, if any. */
export function cameraHeading(camera: Pick<CameraLocationProperties, 'attributes'>): number | null {
  const attrs = camera.attributes || {};
  for (const key of ['camera:direction', 'direction', 'heading', 'bearing', 'azimuth']) {
    const h = parseCameraHeading(attrs[key]);
    if (h != null) return h;
  }
  return null;
}

/** Google Maps Street View deep link; no key required. */
export function streetViewDeepLink(lat: number, lon: number, heading?: number | null, fov = 90): string {
  const h = Math.round(heading ?? 0);
  return `https://www.google.com/maps/@${lat.toFixed(6)},${lon.toFixed(6)},3a,${fov}y,${h}h,90t/data=!3m4!1e1!3m2!1s0:0!2e0`;
}

// ── Thumbnails via the proxy ─────────────────────────────────────────────────

export type StreetViewImageOptions = {
  heading?: number | null;
  fov?: number;
  pitch?: number;
  width?: number;
  height?: number;
};

export type StreetViewImageStatus = 'idle' | 'loading' | 'ready' | 'none' | 'error';

/**
 * CSS size of the standard camera thumbnail (strip tiles). Requested at 2x.
 * Larger renderings (callouts) fall back to this size when their own still
 * cannot be fetched, since it is the one most likely to be cached already.
 */
export const STREET_VIEW_THUMB_W = 136;
export const STREET_VIEW_THUMB_H = 76;

const IMAGE_CACHE_MAX = 200;
/** key → object URL (null = Google has no imagery there). */
const imageCache = new Map<string, string | null>();
const imageInFlight = new Map<string, Promise<string | null>>();

/** Everything but the size: the same view at any resolution is interchangeable in a pinch. */
function viewKey(lat: number, lon: number, o: StreetViewImageOptions): string {
  return [
    lat.toFixed(5), lon.toFixed(5),
    o.heading == null ? '' : Math.round(o.heading),
    o.fov ?? '', o.pitch ?? '',
  ].join('|');
}

function imageKey(lat: number, lon: number, o: StreetViewImageOptions): string {
  return `${viewKey(lat, lon, o)}|${o.width ?? ''}|${o.height ?? ''}`;
}

/** Largest already-loaded rendering of this view at any size, if there is one. */
function cachedAnySize(lat: number, lon: number, o: StreetViewImageOptions): string | null | undefined {
  const prefix = `${viewKey(lat, lon, o)}|`;
  let best: { url: string | null; w: number } | undefined;
  imageCache.forEach((url, key) => {
    if (!key.startsWith(prefix)) return;
    const w = Number(key.slice(prefix.length).split('|')[0]) || 0;
    if (!best || w > best.w) best = { url, w };
  });
  return best?.url;
}

function remember(key: string, url: string | null) {
  if (imageCache.size >= IMAGE_CACHE_MAX) {
    const oldest = imageCache.keys().next().value as string | undefined;
    if (oldest !== undefined) {
      const victim = imageCache.get(oldest);
      if (victim) URL.revokeObjectURL(victim);
      imageCache.delete(oldest);
    }
  }
  imageCache.set(key, url);
}

/**
 * Resolve a Street View thumbnail to an object URL (or null when Google has
 * no imagery within range). Throws on 403 (feature off) / 503 (no key) / other
 * failures so callers can fall back to a deep link.
 *
 * When the requested size fails (upstream error, expired key, ...), reuse the
 * same view at another size: first anything already loaded in this tab, then
 * one attempt at the standard thumbnail size, which the server is most likely
 * to have cached from the cameras-in-view strip. Only if that fails too does
 * the error reach the caller.
 */
export async function fetchStreetViewImageUrl(
  lat: number,
  lon: number,
  opts: StreetViewImageOptions = {},
): Promise<string | null> {
  try {
    return await fetchStreetViewImageUrlExact(lat, lon, opts);
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 403 || status === 503) throw err; // feature off / no key: nothing will help
    const loaded = cachedAnySize(lat, lon, opts);
    if (loaded !== undefined) return loaded;
    const fw = STREET_VIEW_THUMB_W * 2;
    const fh = STREET_VIEW_THUMB_H * 2;
    if (opts.width === fw && opts.height === fh) throw err;
    try {
      return await fetchStreetViewImageUrlExact(lat, lon, { ...opts, width: fw, height: fh });
    } catch {
      throw err;
    }
  }
}

async function fetchStreetViewImageUrlExact(
  lat: number,
  lon: number,
  opts: StreetViewImageOptions,
): Promise<string | null> {
  const key = imageKey(lat, lon, opts);
  if (imageCache.has(key)) return imageCache.get(key) ?? null;
  const pending = imageInFlight.get(key);
  if (pending) return pending;

  const params: Record<string, number> = { lat, lon };
  if (opts.heading != null) params.heading = Math.round(norm(opts.heading));
  if (opts.fov != null) params.fov = opts.fov;
  if (opts.pitch != null) params.pitch = opts.pitch;
  if (opts.width != null) params.width = opts.width;
  if (opts.height != null) params.height = opts.height;

  const run = http
    .get<Blob>('/camera-overlays/street-view/image', { params, responseType: 'blob', timeout: 20_000 })
    .then((r) => {
      const url = URL.createObjectURL(r.data);
      remember(key, url);
      return url;
    })
    .catch((err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        remember(key, null);
        return null;
      }
      throw err;
    })
    .finally(() => {
      imageInFlight.delete(key);
    });
  imageInFlight.set(key, run);
  return run;
}

export type StreetViewMetadata = {
  status: string;
  available: boolean;
  pano_id?: string | null;
  capture_date?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  copyright?: string | null;
  deep_link: string;
};

export async function fetchStreetViewMetadata(lat: number, lon: number, heading?: number | null): Promise<StreetViewMetadata> {
  const params: Record<string, number> = { lat, lon };
  if (heading != null) params.heading = Math.round(norm(heading));
  const r = await http.get<StreetViewMetadata>('/camera-overlays/street-view/metadata', { params, timeout: 20_000 });
  return r.data;
}

/**
 * Hook: load a Street View thumbnail for a point when `enabled`.
 * Returns the object URL plus a status for skeleton / "no imagery" states.
 */
export function useStreetViewImage(
  lat: number | null | undefined,
  lon: number | null | undefined,
  enabled: boolean,
  opts: StreetViewImageOptions = {},
): { url: string | null; status: StreetViewImageStatus; error: string | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<StreetViewImageStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const { heading, fov, pitch, width, height } = opts;

  useEffect(() => {
    if (!enabled || lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      setUrl(null);
      setStatus('idle');
      setError(null);
      return;
    }
    let cancelled = false;
    setStatus('loading');
    setError(null);
    fetchStreetViewImageUrl(lat, lon, { heading, fov, pitch, width, height })
      .then((next) => {
        if (cancelled) return;
        setUrl(next);
        setStatus(next ? 'ready' : 'none');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const detail = (e as { response?: { data?: { detail?: unknown } }; message?: string })?.response?.data?.detail;
        setUrl(null);
        setStatus('error');
        setError(typeof detail === 'string' ? detail : (e as Error)?.message || 'Street View unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, lat, lon, heading, fov, pitch, width, height]);

  return { url, status, error };
}
